import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { getDistance } from "geolib";
import { mappls, mappls_plugin } from "mappls-web-maps";
import {
  ArrowRight,
  BadgeIndianRupee,
  Bus,
  Calendar,
  CalendarClock,
  Car,
  CarFront,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  CirclePlus,
  ClipboardList,
  Clock,
  FileCheck2,
  Globe2,
  Landmark,
  MapPin,
  MessageCircle,
  Phone,
  Plane,
  Route,
  ShieldCheck,
  Star,
  Sparkles,
  UserCheck,
  Users,
} from "lucide-react";
import "./App.css";

const PHONE = "+918124290402";
const WA_LINK = "https://wa.me/918124290402";
const MAPPLS_KEY = "vfccvdtdtrwglhnqaudxmysbkzyldratuaid";
const MAPPLS_TEXTSEARCH = "https://search.mappls.com/search/places/textsearch/json";
const MAPPLS_GEOCODE = "https://atlas.mappls.com/api/places/geocode";

const mapplsClassObject = new mappls();
const mapplsPluginObject = new mappls_plugin();
let mapplsSdkReady = false;
mapplsClassObject.initialize(MAPPLS_KEY, { map: false, plugins: ["getDistance"] }, () => {
  mapplsSdkReady = true;
});

const LANG = {
  en: {
    kicker: "Trip estimator",
    title: "Plan the ride before you call",
    sub: "Pick your vehicle, trip type, and locations — get an instant fare estimate.",
    from: "From",
    to: "To",
    pickupTime: "Pickup time",
    hillStation: "Hill station route",
    hillSurcharge: "(+₹500)",
    acRequired: "AC required",
    getEstimate: "Get Estimate",
    calculating: "Calculating...",
    perKm: "/km",
    perHr: "/hr",
    driverBatta: "driver batta",
    rateLabel: "Selected rate",
  },
  ta: {
    kicker: "கட்டண மதிப்பீட்டு",
    title: "அழைக்கும் முன் பயணத்தை திட்டமிடுங்கள்",
    sub: "வாகனம், பயண வகை மற்றும் இடங்களைத் தேர்வு செய்து உடனடி கட்டண மதிப்பீட்டைப் பெறுங்கள்.",
    from: "எங்கிருந்து",
    to: "எங்கு",
    pickupTime: "பிக்கப் நேரம்",
    hillStation: "மலை பகுதி பாதை",
    hillSurcharge: "(+₹500)",
    acRequired: "குளிரூட்டி தேவை",
    getEstimate: "கட்டணம் காண்க",
    calculating: "கணக்கிடுகிறது...",
    perKm: "/கி.மீ",
    perHr: "/மணி",
    driverBatta: "டிரைவர் பட்டா",
    rateLabel: "தேர்ந்தெடுத்த கட்டணம்",
  },
};

const featuredItems = [
  {
    title: "Chennai ↔ Pondicherry",
    desc: "Most booked route — scenic 160 km coastal drive",
    tag: "Most Popular",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1200&q=80",
    accent: "blue",
  },
  {
    title: "Weekend Hill Getaway",
    desc: "Ooty, Kodaikanal & Munnar — premium SUV packages",
    tag: "Weekend Special",
    image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=1200&q=80",
    accent: "green",
  },
  {
    title: "Airport Transfers",
    desc: "Chennai & Bangalore airports — 24/7 reliable pickup",
    tag: "Always On Time",
    image: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=1200&q=80",
    accent: "violet",
  },
  {
    title: "Group & Corporate Travel",
    desc: "12 to 25-seat coaches for events, tours & office trips",
    tag: "Group Friendly",
    image: "https://images.unsplash.com/photo-1570125909232-eb263c188f7e?auto=format&fit=crop&w=1200&q=80",
    accent: "pink",
  },
];

const CAR_TYPES = [
  {
    id: "sedan",
    name: "Sedan",
    seats: "4 seats",
    tag: "AC",
    image: "https://images.unsplash.com/photo-1619767886558-efdc259cde1a?auto=format&fit=crop&w=800&q=80",
    icon: Car,
    rates: {
      oneWay:    { perKm: 15, batta: 400,  minKm: 130 },
      roundTrip: { perKm: 12, batta: 500,  minKmDay: 250 },
      local:     { perHour: 350, kmPerHr: 10, extraPerKm: 25 },
      airport:   { perKm: 15, batta: 400,  minKm: 30 },
    },
  },
  {
    id: "suv-ertiga",
    name: "SUV Ertiga",
    seats: "6 seats",
    tag: "AC",
    image: "https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&w=800&q=80",
    icon: Car,
    rates: {
      oneWay:    { perKm: 18, batta: 500, minKm: 130 },
      roundTrip: { perKm: 16, batta: 500, minKmDay: 250 },
      local:     { perHour: 400, kmPerHr: 10, extraPerKm: 30 },
      airport:   { perKm: 18, batta: 500, minKm: 30 },
    },
  },
  {
    id: "suv-innova",
    name: "SUV Innova / Kia",
    seats: "7 seats",
    tag: "AC",
    image: "https://images.unsplash.com/photo-1547559418-8d7437f53b5b?auto=format&fit=crop&w=800&q=80",
    icon: Car,
    rates: {
      oneWay:    { perKm: 19, batta: 500, minKm: 130 },
      roundTrip: { perKm: 17, batta: 500, minKmDay: 250 },
      local:     { perHour: 450, kmPerHr: 10, extraPerKm: 35 },
      airport:   { perKm: 19, batta: 500, minKm: 30 },
    },
  },
  {
    id: "suv-crysta",
    name: "SUV Crysta",
    seats: "7 seats",
    tag: "AC",
    image: "https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?auto=format&fit=crop&w=800&q=80",
    icon: Car,
    rates: {
      oneWay:    { perKm: 25, batta: 500, minKm: 130 },
      roundTrip: { perKm: 23, batta: 500, minKmDay: 250 },
      local:     { perHour: 600, kmPerHr: 10, extraPerKm: 50 },
      airport:   { perKm: 25, batta: 500, minKm: 30 },
    },
  },
  {
    id: "tempo12",
    name: "Tempo 12-Seat",
    seats: "12 seats",
    tag: "AC",
    image: "https://images.unsplash.com/photo-1547559418-8d7437f53b5b?auto=format&fit=crop&w=800&q=80",
    icon: Bus,
    rates: {
      oneWay:    { perKm: 24, batta: 1000, minKm: 300 },
      roundTrip: { perKm: 24, batta: 1000, minKmDay: 300 },
      local:     { fiveHr: 3000, tenHr: 5500 },
      airport:   { perKm: 24, batta: 1000, minKm: 50 },
    },
  },
  {
    id: "coach21",
    name: "Coach 21-Seat",
    seats: "21 seats",
    tag: "AC",
    image: "https://images.unsplash.com/photo-1570125909232-eb263c188f7e?auto=format&fit=crop&w=800&q=80",
    icon: Bus,
    rates: {
      oneWay:    { perKm: 30, batta: 1200, minKm: 300 },
      roundTrip: { perKm: 30, batta: 1200, minKmDay: 300 },
      local:     { fiveHr: 5000, tenHr: 9500 },
      airport:   { perKm: 30, batta: 1200, minKm: 50 },
    },
  },
  {
    id: "coach25",
    name: "Coach 25-Seat",
    seats: "25 seats",
    tag: "AC",
    image: "https://images.unsplash.com/photo-1570125909232-eb263c188f7e?auto=format&fit=crop&w=800&q=80",
    icon: Bus,
    rates: {
      oneWay:    { perKm: 33, batta: 1200, minKm: 300 },
      roundTrip: { perKm: 33, batta: 1200, minKmDay: 300 },
      local:     { fiveHr: 5500, tenHr: 10500 },
      airport:   { perKm: 33, batta: 1200, minKm: 50 },
    },
  },
];

const services = [
  {
    icon: Car,
    label: "Self Drive Rental",
    desc: "Clean cars for city trips, family functions, and weekend plans.",
    detail: "Choose a rental car for flexible local movement without waiting for repeat bookings. Best for people who want privacy, route freedom, and simple handover.",
    benefits: ["Flexible pickup time", "Private family travel", "Fuel plan guidance", "Short and long duration rentals"],
    rules: ["Valid driving license required", "Security deposit may apply", "Return with agreed fuel level"],
    policies: ["Damage and late return charges depend on booking terms", "Toll, parking, and permit charges are customer responsibility"],
  },
  {
    icon: Plane,
    label: "Airport Transfer",
    desc: "On-time pickup and drop service with direct driver contact.",
    detail: "Book reliable airport rides with direct call support, route planning, and buffer time for flight changes or traffic delays.",
    benefits: ["Flight-time friendly pickup", "Luggage-ready vehicles", "Doorstep drop", "Early morning and late night support"],
    rules: ["Share flight number and terminal", "Waiting time starts after agreed buffer", "Keep phone reachable"],
    policies: ["Parking and airport entry charges are extra", "Rescheduling depends on driver availability"],
  },
  {
    icon: CalendarClock,
    label: "Daily Hire",
    desc: "Book by the day for business visits, shopping, and temple trips.",
    detail: "Daily hire keeps one vehicle with you for multiple stops, meetings, sightseeing, medical visits, or family work across the city.",
    benefits: ["Multiple stop travel", "No repeated booking calls", "Dedicated vehicle for the day", "Useful for business and family plans"],
    rules: ["Day package has fixed hour and kilometer limits", "Extra usage is billed separately", "Route should be shared early"],
    policies: ["Driver bata may apply for outstation trips", "Night allowance depends on trip timing"],
  },
  {
    icon: Bus,
    label: "Group Travel",
    desc: "SUV, traveller, and bus options for group rides and tours.",
    detail: "Group travel options help families, office teams, school groups, and tour batches move together with planned seating and luggage comfort.",
    benefits: ["One booking for everyone", "Better per-person value", "Luggage space options", "Route coordination support"],
    rules: ["Confirm passenger count before booking", "Large vehicles need route and parking checks", "Seat capacity cannot be exceeded"],
    policies: ["Advance booking recommended", "Permit, toll, parking, and interstate charges may apply"],
  },
  {
    icon: BadgeIndianRupee,
    label: "Budget Packages",
    desc: "Simple rental pricing with flexible ride choices.",
    detail: "Budget packages are made for customers who want clear pricing before travel, with vehicle choices based on distance, comfort, and passenger count.",
    benefits: ["Transparent package discussion", "Vehicle matched to budget", "Good for regular routes", "Simple WhatsApp confirmation"],
    rules: ["Final fare depends on distance and vehicle", "Package limits should be confirmed before start", "Extra stops may change price"],
    policies: ["Advance payment may be requested", "Cancellation terms depend on trip size and timing"],
  },
  {
    icon: ShieldCheck,
    label: "Trusted Drivers",
    desc: "Verified local drivers for safer travel across South India.",
    detail: "Cabnaina focuses on direct driver contact and local travel knowledge, making every ride easier to coordinate and safer to manage.",
    benefits: ["Direct driver communication", "Local route knowledge", "Safer family travel", "Support for urgent ride needs"],
    rules: ["Respect driver rest and legal driving limits", "Share accurate pickup and drop points", "No overloading passengers or luggage"],
    policies: ["Driver assignment depends on availability", "Emergency changes are supported where possible"],
  },
];

const fleet = [
  {
    icon: Car,
    name: "Budget City Car",
    seats: "",
    tag: "Budget",
    capacity: "Passengers with small bags",
    comfort: "Compact, quick, and easy for city movement",
    ideal: "City errands, solo travel, short pickup and drop",
    image: "https://images.unsplash.com/photo-1632414187391-7e4097f0de6b?auto=format&fit=crop&w=1200&q=80",
    features: ["AC cabin", "Budget fare", "Easy parking", "Fast city pickup"],
    facilities: ["Phone charging", "Clean seats", "Music on request"],
  },
  {
    icon: Car,
    name: "Comfort Sedan",
    seats: "",
    tag: "Comfort",
    capacity: "Passengers with medium luggage",
    comfort: "Smooth seating with better leg room for longer rides",
    ideal: "Airport transfer, family functions, business travel",
    image: "https://images.unsplash.com/photo-1619767886558-efdc259cde1a?auto=format&fit=crop&w=1200&q=80",
    features: ["AC comfort", "Boot space", "Smooth highway ride", "Professional driver"],
    facilities: ["Water bottle on request", "Charging point", "Clean interior"],
  },
  {
    icon: Car,
    name: "Premium SUV",
    seats: "",
    tag: "Premium",
    capacity: "Passengers with trip luggage",
    comfort: "Higher seating, stronger road presence, and relaxed long-distance travel",
    ideal: "Hill stations, temple trips, family tours, outstation rides",
    image: "https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&w=1200&q=80",
    features: ["Spacious cabin", "Powerful ride", "Large luggage area", "Tour friendly"],
    facilities: ["Rear AC vents", "Charging support", "Comfort seating"],
  },
  {
    icon: Bus,
    name: "Family Van",
    seats: "",
    tag: "Family",
    capacity: "Group passengers with luggage",
    comfort: "Roomy group seating for families, friends, and small teams",
    ideal: "Tours, wedding travel, office outings, pilgrimage trips",
    image: "https://images.unsplash.com/photo-1547559418-8d7437f53b5b?auto=format&fit=crop&w=1200&q=80",
    features: ["Group seating", "Luggage planning", "Long-route friendly", "Driver coordination"],
    facilities: ["Pushback seats by vehicle", "Music system", "AC options"],
  },
  {
    icon: Bus,
    name: "Group Coach",
    seats: "",
    tag: "Group",
    capacity: "Large group passengers",
    comfort: "Large group movement with planned pickup points and route support",
    ideal: "School trips, company travel, tours, marriage functions",
    image: "https://images.unsplash.com/photo-1570125909232-eb263c188f7e?auto=format&fit=crop&w=1200&q=80",
    features: ["Bulk passenger movement", "Route planning", "Permit support", "Event travel"],
    facilities: ["AC or non-AC options", "Luggage bay by bus type", "Driver and support contact"],
  },
];

const locations = ["Tamil Nadu", "Pondicherry", "Kerala", "Andhra Pradesh", "Karnataka", "South India Tours"];

const tripTypes = [
  { id: "oneWay",    label: "One-Way",       icon: ArrowRight,    accent: "blue" },
  { id: "roundTrip", label: "Round-Trip",    icon: MapPin,        accent: "green" },
  { id: "local",     label: "Local / Daily", icon: CalendarClock, accent: "violet" },
  { id: "airport",   label: "Airport",       icon: Plane,         accent: "pink" },
];

const CAB_DISPLAY_NAMES = ["SEDAN", "ERTIGA", "INNOVA", "CRYSTA"];
const CAB_SEATS = ["4+1", "6+1", "6+1", "7+1"];

const topCities = [
  {
    name: "Chengalpattu",
    image: "https://fasttrackcalltaxi.in/_next/image?url=%2Fimages%2Fgenerated%2Fchengalpattu.webp&w=1200&q=75",
    routes: ["Chennai", "Tirupati", "Pondicherry", "Bangalore", "Coimbatore", "Madurai", "Trichy", "Salem", "Vellore", "Tiruvannamalai"],
  },
  {
    name: "Chennai",
    image: "https://fasttrackcalltaxi.in/_next/image?url=https%3A%2F%2Fft-asset.s3.ap-south-1.amazonaws.com%2Fwebsite%2Fpopular_places%2Fcity_images%2FMarina%2520Beach.png&w=1200&q=75",
    routes: ["Vellore", "Pondicherry", "Thiruvanamalai", "Tirupati", "Tindivanam", "Villupuram", "Sri Kalasthri", "Vandavasi", "Thiruttani", "Tirchy"],
  },
  {
    name: "Chidambaram",
    image: "https://fasttrackcalltaxi.in/_next/image?url=https%3A%2F%2Fft-asset.s3.ap-south-1.amazonaws.com%2Fwebsite%2Fpopular_places%2Fcity_images%2Fchidambaram.jpg&w=1200&q=75",
    routes: ["Chennai", "Tirupati", "Pondicherry", "Bangalore", "Coimbatore", "Madurai", "Trichy", "Salem", "Vellore", "Tiruvannamalai"],
  },
  {
    name: "Coimbatore",
    image: "https://fasttrackcalltaxi.in/_next/image?url=https%3A%2F%2Fft-asset.s3.ap-south-1.amazonaws.com%2Fwebsite%2Fpopular_places%2Fcity_images%2FCoimbatore%2520Easha%2520Temple.png&w=1200&q=75",
    routes: ["Tiruppur", "Pollachi", "Erode", "Palakkad", "Mettupalayam", "Udumalpet", "Palladam", "Palani", "Avinashi", "Karur"],
  },
  {
    name: "Erode",
    image: "https://fasttrackcalltaxi.in/_next/image?url=https%3A%2F%2Fft-asset.s3.ap-south-1.amazonaws.com%2Fwebsite%2Fpopular_places%2Fcity_images%2FERODE.JPG&w=1200&q=75",
    routes: ["Pollachi", "Mettupalayam", "Udumalpet", "Palladam", "Palani", "Karur", "Ooty", "Salem", "Madurai", "Perumanallur"],
  },
  {
    name: "Gummidipoondi",
    image: "https://fasttrackcalltaxi.in/_next/image?url=%2Fcity-placeholder.webp&w=1200&q=75",
    routes: ["Chennai", "Tirupati", "Pondicherry", "Bangalore", "Coimbatore", "Madurai", "Trichy", "Salem", "Vellore", "Tiruvannamalai"],
  },
  {
    name: "Kanchipuram",
    image: "https://fasttrackcalltaxi.in/_next/image?url=%2Fimages%2Fgenerated%2Fkanchipuram.webp&w=1200&q=75",
    routes: ["Chennai", "Tirupati", "Pondicherry", "Bangalore", "Coimbatore", "Madurai", "Trichy", "Salem", "Vellore", "Tiruvannamalai"],
  },
  {
    name: "Karaikudi",
    image: "https://fasttrackcalltaxi.in/_next/image?url=https%3A%2F%2Fft-asset.s3.ap-south-1.amazonaws.com%2Fwebsite%2Fpopular_places%2Fcity_images%2Fkaraikudi.jpg&w=1200&q=75",
    routes: ["Chennai", "Trichy", "Tirupati", "Pondicherry", "Pudukkottai", "Bangalore", "Madurai", "Singampunari", "Kalayarkoil", "Thondi"],
  },
  {
    name: "Karur",
    image: "https://fasttrackcalltaxi.in/_next/image?url=https%3A%2F%2Fft-asset.s3.ap-south-1.amazonaws.com%2Fwebsite%2Fpopular_places%2Fcity_images%2Fkarur.jpg&w=1200&q=75",
    routes: ["Chennai", "Lalapettai", "Velliyanai", "Paramathivellur", "Tirupati", "Pondicherry", "Bangalore", "Coimbatore", "Madurai", "Trichy"],
  },
  {
    name: "Kolkata",
    image: "https://fasttrackcalltaxi.in/_next/image?url=https%3A%2F%2Fft-asset.s3.ap-south-1.amazonaws.com%2Fwebsite%2Fpopular_places%2FPurulia.jpeg&w=1200&q=75",
    routes: ["Purulia", "Odisha", "Puri", "Digha", "Chennai", "Tirupati", "Pondicherry", "Bangalore", "Coimbatore", "Madurai"],
  },
  {
    name: "Madurai",
    image: "https://fasttrackcalltaxi.in/_next/image?url=https%3A%2F%2Fft-asset.s3.ap-south-1.amazonaws.com%2Fwebsite%2Fpopular_places%2Fcity_images%2FMadurai%2520Meenachi.png&w=1200&q=75",
    routes: ["Virudhunagar", "Sivaganga", "Aruppukottai", "Sivakasi", "Theni", "Karaikudi", "Srivilliputhur", "Ramanathapuram", "Rajapalayam", "Batlagundu"],
  },
  {
    name: "Mahabalipuram",
    image: "https://fasttrackcalltaxi.in/_next/image?url=%2Fimages%2Fgenerated%2Fmahabalipuram.webp&w=1200&q=75",
    routes: ["Chennai", "Tirupati", "Pondicherry", "Bangalore", "Coimbatore", "Madurai", "Trichy", "Salem", "Vellore", "Tiruvannamalai"],
  },
  {
    name: "Nagercoil",
    image: "https://fasttrackcalltaxi.in/_next/image?url=https%3A%2F%2Fft-asset.s3.ap-south-1.amazonaws.com%2Fwebsite%2Fpopular_places%2Fcity_images%2FNagercoil.jpg&w=1200&q=75",
    routes: ["Pollachi", "Marthandam", "Kulasekharam", "Thirparappu Falls", "Erode", "Mettupalayam", "Chennai", "Palani", "Tirupati", "Karur"],
  },
  {
    name: "Perambalur",
    image: "https://fasttrackcalltaxi.in/_next/image?url=https%3A%2F%2Fft-asset.s3.ap-south-1.amazonaws.com%2Fwebsite%2Fpopular_places%2Fcity_images%2Fperambalur.jpg&w=1200&q=75",
    routes: ["Chennai", "Tirupati", "Karur", "Ponparappi", "Somarasampettai", "Ammapettai", "Veeraganur Bus Stand", "Lappaikudikadu", "Subramaniapuram Trichy", "Perumulai Muthaiya Temple"],
  },
  {
    name: "Salem",
    image: "https://fasttrackcalltaxi.in/_next/image?url=%2Fcity-placeholder.webp&w=1200&q=75",
    routes: ["Namakkal", "Mettur", "Bhavani", "Vazhapadi", "Rasipuram", "Dharmapuri", "Sankagiri", "Coimbatore", "Nangavalli", "Vanavasi"],
  },
  {
    name: "Sivaganga",
    image: "https://fasttrackcalltaxi.in/_next/image?url=%2Fimages%2Fgenerated%2Fsivaganga.webp&w=1200&q=75",
    routes: ["Chennai", "Tirupati", "Pondicherry", "Bangalore", "Coimbatore", "Madurai", "Trichy", "Salem", "Vellore", "Tiruvannamalai"],
  },
];

const TOTAL_CITIES_COVERED = new Set(
  topCities.flatMap((city) => [city.name, ...city.routes])
).size;

function FloatingIcon({ Icon, delay, top, left, size }) {
  return (
    <motion.div
      className="floating-icon"
      style={{ top, left, width: size, height: size }}
      animate={{ y: [0, -18, 0], rotate: [0, 7, -5, 0] }}
      transition={{ duration: 4.5, delay, repeat: Infinity, ease: "easeInOut" }}
    >
      <Icon size={Math.max(22, size * 0.42)} />
    </motion.div>
  );
}

function useCounter(target, duration, disabled) {
  const [count, setCount] = useState(disabled ? target : 0);
  const done = useRef(false);

  useEffect(() => {
    if (disabled || done.current) return;
    done.current = true;
    const step = target / ((duration || 2000) / 16);
    let current = 0;
    const timer = setInterval(() => {
      current = Math.min(current + step, target);
      setCount(Math.floor(current));
      if (current >= target) clearInterval(timer);
    }, 16);

    return () => clearInterval(timer);
  }, [target, duration, disabled]);

  return count;
}

function StatCard({ value, suffix, label, staticValue = false }) {
  const count = useCounter(value, 1700, staticValue);

  return (
    <motion.div className="stat-card" whileHover={{ y: -5 }}>
      <span className="stat-num">{count}{suffix}</span>
      <span className="stat-label">{label}</span>
    </motion.div>
  );
}

function routeSlug(value) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

async function searchPlaces(query) {
  if (!query || query.trim().length < 3) return [];
  try {
    const r = await fetch(
      `${MAPPLS_TEXTSEARCH}?query=${encodeURIComponent(query)}&region=ind&access_token=${MAPPLS_KEY}`
    );
    if (!r.ok) throw new Error("Mappls textsearch failed");
    const d = await r.json();
    const places = [...(d.suggestedLocations || []), ...(d.userAddedLocations || [])];
    // Mappls textsearch doesn't return coordinates — resolved lazily on selection via geocodeLabel().
    return places.map((p) => ({
      label: [p.placeName, p.placeAddress].filter(Boolean).join(", "),
      lat: null,
      lon: null,
      eLoc: p.eLoc,
    }));
  } catch {
    return [];
  }
}

async function geocodeLabel(label) {
  try {
    const r = await fetch(
      `${MAPPLS_GEOCODE}?address=${encodeURIComponent(label)}&access_token=${MAPPLS_KEY}`
    );
    if (!r.ok) throw new Error("Mappls geocode failed");
    const d = await r.json();
    const hit = d.results?.[0];
    if (!hit || hit.lat == null || hit.lng == null) return null;
    return { lat: hit.lat, lon: hit.lng };
  } catch {
    return null;
  }
}


function getRouteTraffic(from, to) {
  const fromCoord = from.eLoc || (from.lat && from.lon ? `${from.lat},${from.lon}` : null);
  const toCoord = to.eLoc || (to.lat && to.lon ? `${to.lat},${to.lon}` : null);
  if (!mapplsSdkReady || !fromCoord || !toCoord) return Promise.resolve(null);

  return new Promise((resolve) => {
    try {
      mapplsPluginObject.getDistance(
        { coordinates: `${fromCoord};${toCoord}`, resource: "distance_matrix_eta", region: "IND" },
        (data) => {
          const distM = data?.results?.distances?.[0]?.[1];
          const durS = data?.results?.durations?.[0]?.[1];
          const durTrafficS = data?.results?.durations_in_traffic?.[0]?.[1];
          if (!distM) { resolve(null); return; }
          resolve({
            km: Math.max(1, Math.ceil(distM / 1000)),
            durationMin: durS ? Math.ceil(durS / 60) : null,
            trafficMin: durTrafficS ? Math.ceil(durTrafficS / 60) : (durS ? Math.ceil(durS / 60) : null),
            delayMin: durTrafficS && durS ? Math.max(0, Math.ceil((durTrafficS - durS) / 60)) : 0,
          });
        }
      );
    } catch {
      resolve(null);
    }
  });
}

function getEstimatedFare(carType, tripTypeId, km) {
  const r = carType.rates[tripTypeId] ?? carType.rates.oneWay;
  if (r.perHour !== undefined) {
    const hrs = 4;
    const includedKm = hrs * r.kmPerHr;
    const extraKm = Math.max(0, km - includedKm);
    return Math.round(hrs * r.perHour + extraKm * r.extraPerKm);
  }
  if (r.fiveHr !== undefined) {
    return r.fiveHr;
  }
  const effKm = tripTypeId === "roundTrip"
    ? Math.max(km * 2, r.minKmDay ?? 1)
    : Math.max(km, r.minKm ?? 1);
  return Math.round(effKm * r.perKm + r.batta);
}

export default function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeService, setActiveService] = useState(0);
  const [activeFleet, setActiveFleet] = useState(1);
  const [activeCity, setActiveCity] = useState(0);
  const [activeTripType, setActiveTripType] = useState(0);
  const [activeCarType, setActiveCarType] = useState(0);
  const [lang, setLang] = useState("en");
  const [tripForm, setTripForm] = useState({
    name: "", mobile: "", from: "", to: "",
    pickupDate: new Date().toISOString().split("T")[0],
    pickupTime: "12:00 AM",
    hillArea: false, acRequired: true,
  });
  const [tripPlaces, setTripPlaces] = useState({ from: null, to: null });
  const [placeSuggestions, setPlaceSuggestions] = useState({ from: [], to: [] });
  const [activeSuggestField, setActiveSuggestField] = useState(null);
  const [estimateResult, setEstimateResult] = useState(null);
  const [estimateError, setEstimateError] = useState("");
  const [isEstimating, setIsEstimating] = useState(false);
  const [formErrors, setFormErrors] = useState({ name: false, mobile: false });
  const timeOptions = Array.from({ length: 48 }, (_, i) => {
    const h = Math.floor(i / 2);
    const m = i % 2 === 0 ? "00" : "30";
    const ampm = h < 12 ? "AM" : "PM";
    const hour = h % 12 === 0 ? 12 : h % 12;
    return `${hour}:${m} ${ampm}`;
  });
  const [mouse, setMouse] = useState({ x: 50, y: 20 });
  const serviceCarouselRef = useRef(null);
  const cityCarouselRef = useRef(null);
  const fleetCarouselRef = useRef(null);
  const featuredCarouselRef = useRef(null);
  const dateInputRef = useRef(null);

  const openDatePicker = () => {
    const input = dateInputRef.current;
    if (!input) return;
    if (typeof input.showPicker === "function") {
      try { input.showPicker(); return; } catch { /* fall through */ }
    }
    input.focus();
  };
  const t = LANG[lang];
  const navItems = ["Featured", "Estimator", "Services", "Cities", "Fleet", "Locations", "Careers", "Contact"];
  const selectedService = services[activeService];
  const selectedFleet = fleet[activeFleet];
  const selectedCity = topCities[activeCity];
  const selectedTripType = tripTypes[activeTripType];
  const selectedCarType = CAR_TYPES[activeCarType];
  const tripKey = activeTripType === 1 ? "roundTrip" : "oneWay";
  const currentPerKm = (selectedCarType.rates[tripKey] ?? selectedCarType.rates.oneWay).perKm;
  const activeSuggestionQuery = activeSuggestField === "from" ? tripForm.from : activeSuggestField === "to" ? tripForm.to : "";
  const previewKmMap = { oneWay: 95, roundTrip: 180, local: 40, airport: 28 };
  const previewKm = previewKmMap[selectedTripType.id] ?? 30;
  const displayedKm = estimateResult?.km || previewKm;
  const hillSurcharge = tripForm.hillArea ? 500 : 0;
  const displayedFare = estimateResult?.fare || (getEstimatedFare(selectedCarType, selectedTripType.id, previewKm) + hillSurcharge);

  const autoPausedUntil = useRef(0);

  const scrollCarousel = (ref, direction) => {
    ref.current?.scrollBy({ left: direction * 340, behavior: "smooth" });
    autoPausedUntil.current = Date.now() + 3000;
  };
  const autoScrollCarousel = (ref) => {
    if (Date.now() < autoPausedUntil.current) return;
    const track = ref.current;
    if (!track) return;
    if (track.scrollLeft <= 1) {
      track.scrollLeft = track.scrollWidth - track.clientWidth;
      return;
    }
    track.scrollTo({ left: track.scrollLeft - 1, behavior: "auto" });
  };

  useEffect(() => {
    const timer = setInterval(() => {
      [serviceCarouselRef, cityCarouselRef, fleetCarouselRef, featuredCarouselRef].forEach(autoScrollCarousel);
    }, 35);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!activeSuggestField) return;
      try {
        const results = await searchPlaces(activeSuggestionQuery);
        setPlaceSuggestions((current) => ({ ...current, [activeSuggestField]: results }));
      } catch {
        setPlaceSuggestions((current) => ({ ...current, [activeSuggestField]: [] }));
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [activeSuggestField, activeSuggestionQuery]);

  const updateTripField = (field, value) => {
    setTripForm((current) => ({ ...current, [field]: value }));
    setTripPlaces((current) => ({ ...current, [field]: null }));
    setEstimateResult(null);
    setEstimateError("");
    setActiveSuggestField(field);
  };

  const selectPlace = (field, place) => {
    setTripForm((current) => ({ ...current, [field]: place.label }));
    setTripPlaces((current) => ({ ...current, [field]: place }));
    setPlaceSuggestions((current) => ({ ...current, [field]: [] }));
    setActiveSuggestField(null);
  };

  const resolvePlace = async (field) => {
    let place = tripPlaces[field];
    if (!place) {
      const results = await searchPlaces(tripForm[field]);
      if (!results.length) throw new Error(`Select a valid ${field === "from" ? "pickup" : "drop"} location`);
      place = results[0];
      selectPlace(field, place);
    }
    if (!place.lat || !place.lon) {
      const coords = await geocodeLabel(place.label);
      if (coords) {
        place = { ...place, ...coords };
        setTripPlaces((current) => ({ ...current, [field]: place }));
      }
    }
    return place;
  };

  const handleEstimateTrip = async () => {
    const nameOk = tripForm.name.trim().length >= 2;
    const mobileOk = /^[6-9]\d{9}$/.test(tripForm.mobile.replace(/\D/g, ""));
    if (!nameOk || !mobileOk) {
      setFormErrors({ name: !nameOk, mobile: !mobileOk });
      return;
    }
    setFormErrors({ name: false, mobile: false });
    setEstimateError("");
    setIsEstimating(true);
    try {
      if (!tripForm.from.trim() || !tripForm.to.trim()) {
        throw new Error("Enter both pickup and drop locations");
      }
      const from = await resolvePlace("from");
      const to = await resolvePlace("to");

      const hasCoords = from.lat && from.lon && to.lat && to.lon;
      const route = await getRouteTraffic(from, to);
      let km;
      if (route) {
        km = route.km;
      } else if (hasCoords) {
        const meters = getDistance(
          { latitude: from.lat, longitude: from.lon },
          { latitude: to.lat, longitude: to.lon }
        );
        km = Math.max(1, Math.ceil((meters / 1000) * 1.18));
      } else {
        km = previewKm;
      }

      const fare = getEstimatedFare(selectedCarType, selectedTripType.id, km) + hillSurcharge;
      const trafficStatus = !route ? null
        : route.delayMin >= 15 ? "heavy"
        : route.delayMin >= 5  ? "moderate"
        : "clear";

      setEstimateResult({
        km, fare, from, to,
        approximate: !route && !hasCoords,
        trafficMin: route?.trafficMin ?? null,
        durationMin: route?.durationMin ?? null,
        delayMin: route?.delayMin ?? null,
        trafficStatus,
      });
    } catch (error) {
      setEstimateError(error.message || "Unable to estimate this trip");
    } finally {
      setIsEstimating(false);
    }
  };

  return (
    <div
      className="app"
      style={{ "--mouse-x": `${mouse.x}%`, "--mouse-y": `${mouse.y}%` }}
      onMouseMove={(event) => {
        const x = (event.clientX / window.innerWidth) * 100;
        const y = (event.clientY / window.innerHeight) * 100;
        setMouse({ x, y });
      }}
    >
      <div className="mouse-glow" />
      <motion.nav className="nav" initial={{ y: -80 }} animate={{ y: 0 }} transition={{ duration: 0.55 }}>
        <div className="nav-inner">
          <a href="#home" className="nav-logo" aria-label="Cabnaina home">
            <img src="/favicon.svg" alt="Cabnaina" className="logo-mark" style={{ objectFit: "contain" }} />
            <span className="logo-text">cab<b>naina</b></span>
          </a>

          <div className="nav-links">
            {navItems.map((item) => (
              <a key={item} href={`#${item.toLowerCase()}`} className="nav-link">{item}</a>
            ))}
          </div>

          <a href="/tariff.html" target="_blank" rel="noreferrer" className="nav-link nav-link-tariff">Tariff</a>
          <a href="/admin" className="nav-link nav-link-admin">Admin</a>
          <a href={`tel:${PHONE}`} className="nav-cta"><Phone size={16} /> Call Now</a>
          <button className="hamburger" onClick={() => setMenuOpen(!menuOpen)} aria-label="Open menu">
            <CirclePlus size={22} />
          </button>
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.div className="mobile-menu" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
              {navItems.map((item) => (
                <a key={item} href={`#${item.toLowerCase()}`} className="mob-link" onClick={() => setMenuOpen(false)}>{item}</a>
              ))}
              <a href={`tel:${PHONE}`} className="mob-cta"><Phone size={16} /> Call Now</a>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* ── Hero with Estimator ── */}
      <section className="hero hero-estimator" id="home">
        <FloatingIcon Icon={Car} delay={0} top="10%" left="2%" size={60} />
        <FloatingIcon Icon={Sparkles} delay={0.6} top="12%" left="90%" size={70} />
        <FloatingIcon Icon={MapPin} delay={1.1} top="84%" left="2%" size={52} />
        <FloatingIcon Icon={MessageCircle} delay={1.6} top="86%" left="91%" size={62} />

        <motion.div className="trip-estimator" initial={{ opacity: 0, y: 35 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.3 }}>

          {/* ── Lang toggle ── */}
          <div className="lang-toggle" style={{ margin: "0 auto" }}>
            <button type="button" className={`lang-btn ${lang === "en" ? "is-active" : ""}`} onClick={() => setLang("en")}>EN</button>
            <button type="button" className={`lang-btn ${lang === "ta" ? "is-active" : ""}`} onClick={() => setLang("ta")}>தமிழ்</button>
          </div>

          <div className="te-stage">
          <div className={`te-form-layer ${estimateResult ? "te-layer-hidden" : "te-layer-visible"}`}>
              {/* ── Trip type toggle ── */}
              <div className="te-type-row">
                <button type="button" className={`te-type-btn ${activeTripType === 0 ? "te-type-active" : ""}`}
                  onClick={() => { setActiveTripType(0); setEstimateResult(null); }}>
                  One Way
                </button>
                <button type="button" className={`te-type-btn ${activeTripType === 1 ? "te-type-active" : ""}`}
                  onClick={() => { setActiveTripType(1); setEstimateResult(null); }}>
                  Round Trip
                </button>
              </div>

              {/* ── Validation errors ── */}
              {(formErrors.name || formErrors.mobile) && (
                <div className="te-errors">
                  {formErrors.name && <p className="te-error-msg">Enter valid name</p>}
                  {formErrors.mobile && <p className="te-error-msg">Enter valid mobile number</p>}
                </div>
              )}

              {/* ── Name + Mobile ── */}
              <div className="te-2col">
                <div className="te-input-box">
                  <span className="te-input-label">NAME</span>
                  <input
                    className="te-text-input"
                    type="text"
                    placeholder="Full Name"
                    value={tripForm.name}
                    onChange={(e) => {
                      setTripForm((f) => ({ ...f, name: e.target.value }));
                      setFormErrors((err) => ({ ...err, name: false }));
                    }}
                  />
                </div>
                <div className="te-input-box">
                  <span className="te-input-label">MOBILE</span>
                  <input
                    className="te-text-input"
                    type="tel"
                    placeholder="Mobile Number"
                    value={tripForm.mobile}
                    onChange={(e) => {
                      setTripForm((f) => ({ ...f, mobile: e.target.value }));
                      setFormErrors((err) => ({ ...err, mobile: false }));
                    }}
                  />
                </div>
              </div>

              {/* ── FROM ── */}
              <div className="te-input-box te-loc-box">
                <span className="te-input-label">FROM</span>
                <div className="autocomplete-wrap">
                  <div className="te-loc-row">
                    <i className="dot green" />
                    <input
                      className="te-text-input"
                      type="text"
                      placeholder="Pickup location"
                      value={tripForm.from}
                      onFocus={() => setActiveSuggestField("from")}
                      onChange={(e) => updateTripField("from", e.target.value)}
                    />
                  </div>
                  {placeSuggestions.from.length > 0 && activeSuggestField === "from" && (
                    <div className="suggestion-list">
                      {placeSuggestions.from.map((place, i) => (
                        <button key={i} type="button" onClick={() => selectPlace("from", place)}>
                          <MapPin size={14} /> {place.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* ── TO ── */}
              <div className="te-input-box te-loc-box">
                <span className="te-input-label">TO</span>
                <div className="autocomplete-wrap">
                  <div className="te-loc-row">
                    <i className="dot red" />
                    <input
                      className="te-text-input"
                      type="text"
                      placeholder="Drop location"
                      value={tripForm.to}
                      onFocus={() => setActiveSuggestField("to")}
                      onChange={(e) => updateTripField("to", e.target.value)}
                    />
                  </div>
                  {placeSuggestions.to.length > 0 && activeSuggestField === "to" && (
                    <div className="suggestion-list">
                      {placeSuggestions.to.map((place, i) => (
                        <button key={i} type="button" onClick={() => selectPlace("to", place)}>
                          <MapPin size={14} /> {place.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Pickup Date + Time ── */}
              <div className="te-2col">
                <div className="te-input-box">
                  <span className="te-input-label">PICKUP DATE</span>
                  <div className="te-icon-row" onClick={openDatePicker}>
                    <input
                      ref={dateInputRef}
                      className="te-text-input te-date-input"
                      type="date"
                      min={new Date().toISOString().split("T")[0]}
                      value={tripForm.pickupDate}
                      onChange={(e) => setTripForm((f) => ({ ...f, pickupDate: e.target.value }))}
                    />
                    <Calendar size={15} className="te-field-icon te-field-icon-clickable" />
                  </div>
                </div>
                <div className="te-input-box">
                  <span className="te-input-label">PICKUP TIME</span>
                  <div className="te-icon-row">
                    <select
                      className="te-text-input te-select"
                      value={tripForm.pickupTime}
                      onChange={(e) => setTripForm((f) => ({ ...f, pickupTime: e.target.value }))}
                    >
                      {timeOptions.map((opt) => <option key={opt}>{opt}</option>)}
                    </select>
                    <ChevronDown size={15} className="te-field-icon" />
                  </div>
                </div>
              </div>

              {/* ── SELECT YOUR CAB ── */}
              <div className="te-cab-section">
                <p className="te-cab-title">SELECT YOUR CAB</p>
                <div className="te-cab-grid">
                  {CAR_TYPES.slice(0, 4).map((car, index) => {
                    const perKm = car.rates[tripKey]?.perKm ?? car.rates.oneWay.perKm;
                    return (
                      <button
                        key={car.id}
                        type="button"
                        className={`te-cab-card ${activeCarType === index ? "te-cab-active" : ""}`}
                        onClick={() => { setActiveCarType(index); setEstimateResult(null); }}
                      >
                        <span className="te-cab-rate">{perKm} ₹ / Km</span>
                        <div className="te-cab-img">
                          <img src={car.image} alt={car.name} loading="lazy" />
                        </div>
                        <span className="te-cab-name">{CAB_DISPLAY_NAMES[index]} {CAB_SEATS[index]}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {estimateError && <p className="estimate-error">{estimateError}</p>}

              {/* ── GET ESTIMATION button ── */}
              <button
                className="te-get-btn"
                type="button"
                onClick={handleEstimateTrip}
                disabled={isEstimating}
              >
                {isEstimating ? t.calculating : "GET ESTIMATION"}
              </button>
          </div>

          <div className={`te-estimate-panel te-estimate-layer ${estimateResult ? "te-layer-visible" : "te-layer-hidden"}`}>
            {estimateResult && (
              <>
                <button type="button" className="te-back-btn" onClick={() => setEstimateResult(null)}>
                  <ChevronLeft size={16} /> Change trip details
                </button>

                <div className="te-estimate-card">
                  <span className="te-estimate-kicker">Estimated Fare</span>
                  <span className="te-estimate-sub">For personal use</span>
                  <div className="te-estimate-fare-block">
                    <span className="te-estimate-fare">₹{displayedFare.toLocaleString("en-IN")}</span>
                    {estimateResult.approximate && (
                      <span className="te-estimate-approx">~ Approx. distance, confirmed on call</span>
                    )}
                  </div>

                  {estimateResult.trafficStatus && (
                    <div className="traffic-row traffic-row-center">
                      <span className={`traffic-chip traffic-${estimateResult.trafficStatus}`}>
                        <span className="traffic-dot" />
                        {estimateResult.trafficStatus === "clear"
                          ? "Traffic clear"
                          : estimateResult.trafficStatus === "moderate"
                          ? "Moderate traffic"
                          : "Heavy traffic"}
                      </span>
                      {estimateResult.trafficMin && (
                        <span className="traffic-time">
                          <Clock size={13} /> {estimateResult.trafficMin} min
                        </span>
                      )}
                      {estimateResult.delayMin > 0 && (
                        <span className="traffic-delay">+{estimateResult.delayMin} min delay</span>
                      )}
                    </div>
                  )}
                </div>

                <div className="te-estimate-rows">
                  <div className="te-estimate-row">
                    <span><Route size={16} className="te-estimate-icon" /> Total Distance</span>
                    <b>{estimateResult.approximate ? "~" : ""}{displayedKm} KM</b>
                  </div>
                  <div className="te-estimate-row">
                    <span><BadgeIndianRupee size={16} className="te-estimate-icon" /> Rate</span>
                    <b>₹{currentPerKm}/km</b>
                  </div>
                  <div className="te-estimate-row">
                    <span><CarFront size={16} className="te-estimate-icon" /> Vehicle</span>
                    <b>{CAB_DISPLAY_NAMES[activeCarType]} {CAB_SEATS[activeCarType]}</b>
                  </div>
                  <div className="te-estimate-row">
                    <span><UserCheck size={16} className="te-estimate-icon" /> Driver Allowance</span>
                    <b>Included</b>
                  </div>
                  <div className="te-estimate-row">
                    <span><Landmark size={16} className="te-estimate-icon" /> Toll</span>
                    <b>Extra</b>
                  </div>
                  <div className="te-estimate-row">
                    <span><FileCheck2 size={16} className="te-estimate-icon" /> Permit</span>
                    <b>Extra</b>
                  </div>
                </div>

                <a
                  className="te-wa-link te-wa-link-lg"
                  href={`${WA_LINK}?text=${encodeURIComponent(`Hi Cabnaina, I want to book a cab.\nName: ${tripForm.name}\nMobile: ${tripForm.mobile}\nVehicle: ${selectedCarType.name}\nTrip: ${activeTripType === 0 ? "One Way" : "Round Trip"}\nFrom: ${tripForm.from || "-"}\nTo: ${tripForm.to || "-"}\nDate: ${tripForm.pickupDate}\nTime: ${tripForm.pickupTime}\nEstimate: Rs ${displayedFare.toLocaleString("en-IN")} / ${displayedKm} km`)}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <MessageCircle size={15} /> Book on WhatsApp
                </a>
              </>
            )}
          </div>
          </div>

        </motion.div>

        <motion.div className="stats-row" initial={{ opacity: 0, y: 45 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4 }}>
          <StatCard value={services.length} suffix="+" label="Rental Services" />
          <StatCard value={CAR_TYPES.length} suffix="+" label="Vehicle Types" />
          <StatCard value={TOTAL_CITIES_COVERED} suffix="+" label="Cities" />
          <StatCard value={24} suffix="/7" label="Support" staticValue />
        </motion.div>
      </section>

      {/* ── Featured Carousel ── */}
      <section className="section featured-section" id="featured">
        <motion.div className="section-head" initial={{ opacity: 0, y: 35 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <span className="section-kicker">Featured trips</span>
          <h2 className="section-title">Popular routes &amp; packages</h2>
          <p className="section-sub">Top-booked journeys across South India — tap to enquire instantly.</p>
        </motion.div>

        <div className="carousel-shell">
          <div className="carousel-controls" aria-label="Featured carousel controls">
            <button type="button" onClick={() => scrollCarousel(featuredCarouselRef, -1)} aria-label="Previous">
              <ChevronLeft size={20} />
            </button>
            <button type="button" onClick={() => scrollCarousel(featuredCarouselRef, 1)} aria-label="Next">
              <ChevronRight size={20} />
            </button>
          </div>
          <div className="featured-track carousel-track" ref={featuredCarouselRef}>
            {featuredItems.map((item, index) => (
              <motion.a
                key={item.title}
                href={`${WA_LINK}?text=${encodeURIComponent(`Hi Cabnaina, I'm interested in: ${item.title}`)}`}
                target="_blank"
                rel="noreferrer"
                className={`featured-card featured-card-${item.accent}`}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.42, delay: index * 0.07 }}
                whileHover={{ y: -6, scale: 1.01 }}
              >
                <div className="featured-card-img">
                  <img src={item.image} alt={item.title} loading="lazy" />
                </div>
                <div className="featured-card-content">
                  <span className={`featured-tag featured-tag-${item.accent}`}>{item.tag}</span>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                  <span className="card-action">Book now <ArrowRight size={14} /></span>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>


      {/* ── Services ── */}
      <section className="section" id="services">
        <motion.div className="section-head" initial={{ opacity: 0, y: 35 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <span className="section-kicker">Rental services</span>
          <h2 className="section-title">Book smarter, ride brighter</h2>
          <p className="section-sub">Cabnaina brings rental requests, direct calls, and cab partner postings into one friendly place.</p>
        </motion.div>

        <div className="carousel-shell">
          <div className="carousel-controls" aria-label="Rental services carousel controls">
            <button type="button" onClick={() => scrollCarousel(serviceCarouselRef, -1)} aria-label="Previous rental service">
              <ChevronLeft size={20} />
            </button>
            <button type="button" onClick={() => scrollCarousel(serviceCarouselRef, 1)} aria-label="Next rental service">
              <ChevronRight size={20} />
            </button>
          </div>
          <div className="services-grid carousel-track" ref={serviceCarouselRef}>
            {services.map(({ icon: Icon, label, desc }, index) => (
              <motion.button
                key={label}
                className={`service-card ${activeService === index ? "is-active" : ""}`}
                initial={{ opacity: 0, y: 35 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: index * 0.06 }}
                whileHover={{ y: -8, scale: 1.01 }}
                onClick={() => setActiveService(index)}
              >
                <span className="s-icon"><Icon size={26} /></span>
                <h3 className="s-label">{label}</h3>
                <p className="s-desc-static">{desc}</p>
                <span className="card-action">View details <ArrowRight size={15} /></span>
              </motion.button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.article
            key={selectedService.label}
            className="detail-panel service-detail"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.28 }}
          >
            <div>
              <span className="detail-pill">Selected service</span>
              <h3>{selectedService.label}</h3>
              <p>{selectedService.detail}</p>
            </div>
            <div className="detail-columns">
              <div>
                <h4>Travel benefits</h4>
                {selectedService.benefits.map((item) => <span key={item}><CheckCircle2 size={15} /> {item}</span>)}
              </div>
              <div>
                <h4>Rules</h4>
                {selectedService.rules.map((item) => <span key={item}><ShieldCheck size={15} /> {item}</span>)}
              </div>
              <div>
                <h4>Policies</h4>
                {selectedService.policies.map((item) => <span key={item}><ClipboardList size={15} /> {item}</span>)}
              </div>
            </div>
          </motion.article>
        </AnimatePresence>
      </section>

      {/* ── Cities ── */}
      <section className="section section-alt" id="cities">
        <motion.div className="section-head" initial={{ opacity: 0, y: 35 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <span className="section-kicker">Our top cities</span>
          <h2 className="section-title">Popular cab routes by city</h2>
          <p className="section-sub">Pick a city to view its most requested outstation cab services below.</p>
        </motion.div>

        <div className="carousel-shell">
          <div className="carousel-controls" aria-label="Top cities carousel controls">
            <button type="button" onClick={() => scrollCarousel(cityCarouselRef, -1)} aria-label="Previous city">
              <ChevronLeft size={20} />
            </button>
            <button type="button" onClick={() => scrollCarousel(cityCarouselRef, 1)} aria-label="Next city">
              <ChevronRight size={20} />
            </button>
          </div>
          <div className="city-track carousel-track" ref={cityCarouselRef}>
            {topCities.map((city, index) => (
              <motion.button
                key={city.name}
                className={`city-card ${activeCity === index ? "is-active" : ""}`}
                initial={{ opacity: 0, y: 35 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: index * 0.03 }}
                whileHover={{ y: -7 }}
                onClick={() => setActiveCity(index)}
              >
                <img src={fleet[index % fleet.length].image} alt={`${city.name} silver cab service`} loading="lazy" />
                <div className="city-copy">
                  <h3>Call taxi &amp; Cab service from {city.name}</h3>
                  <p>{city.routes.slice(0, 5).join(", ")} and more</p>
                  <span className="card-action">Show routes <ArrowRight size={15} /></span>
                </div>
              </motion.button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.article
            key={selectedCity.name}
            className="detail-panel city-detail"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.28 }}
          >
            <div>
              <span className="detail-pill">Selected city</span>
              <h3>Call taxi &amp; Cab service from {selectedCity.name}</h3>
              <p>Quick outstation cab links from {selectedCity.name} to nearby and popular travel destinations.</p>
            </div>
            <div className="route-links">
              {selectedCity.routes.map((route) => (
                <a
                  key={route}
                  href={`/${routeSlug(selectedCity.name)}/outstation/${routeSlug(selectedCity.name)}-to-${routeSlug(route)}`}
                  title={`${selectedCity.name} to ${route} cab service`}
                >
                  <MapPin size={15} /> {route}
                </a>
              ))}
            </div>
          </motion.article>
        </AnimatePresence>
      </section>

      {/* ── Fleet ── */}
      <section className="section section-alt" id="fleet">
        <motion.div className="section-head" initial={{ opacity: 0, y: 35 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <span className="section-kicker">Fleet choices</span>
          <h2 className="section-title">Cars for every rental plan</h2>
          <p className="section-sub">From simple city rides to group tours, show the right option quickly.</p>
        </motion.div>

        <div className="carousel-shell">
          <div className="carousel-controls" aria-label="Fleet carousel controls">
            <button type="button" onClick={() => scrollCarousel(fleetCarouselRef, -1)} aria-label="Previous vehicle">
              <ChevronLeft size={20} />
            </button>
            <button type="button" onClick={() => scrollCarousel(fleetCarouselRef, 1)} aria-label="Next vehicle">
              <ChevronRight size={20} />
            </button>
          </div>
          <div className="fleet-grid carousel-track" ref={fleetCarouselRef}>
            {fleet.map(({ icon: Icon, name, tag }, index) => (
              <motion.button
                key={name}
                className={`car-card ${activeFleet === index ? "is-active" : ""}`}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: index * 0.08 }}
                whileHover={{ y: -8 }}
                onClick={() => setActiveFleet(index)}
              >
                <div className="car-tag">{tag}</div>
                <motion.span className="car-icon" animate={{ y: [0, -7, 0] }} transition={{ duration: 2.4 + index * 0.2, repeat: Infinity }}>
                  <Icon size={42} />
                </motion.span>
                <span className="card-action">See features <ArrowRight size={15} /></span>
              </motion.button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.article
            key={selectedFleet.name}
            className="detail-panel fleet-detail"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.28 }}
          >
            <div className="fleet-image-wrap">
              <img src={selectedFleet.image} alt={`${selectedFleet.name} rental vehicle`} />
              <span>{selectedFleet.tag}</span>
            </div>
            <div className="fleet-info">
              <span className="detail-pill">Selected car type</span>
              <h3>{selectedFleet.tag} vehicle</h3>
              <p>{selectedFleet.comfort}</p>
              <div className="fleet-meta">
                <span><Users size={16} /> {selectedFleet.capacity}</span>
                <span><Star size={16} /> Ideal for: {selectedFleet.ideal}</span>
              </div>
              <div className="detail-columns compact">
                <div>
                  <h4>Features</h4>
                  {selectedFleet.features.map((item) => <span key={item}><CheckCircle2 size={15} /> {item}</span>)}
                </div>
                <div>
                  <h4>Facilities</h4>
                  {selectedFleet.facilities.map((item) => <span key={item}><Sparkles size={15} /> {item}</span>)}
                </div>
              </div>
            </div>
          </motion.article>
        </AnimatePresence>
      </section>

      {/* ── Locations ── */}
      <section className="section section-alt" id="locations">
        <motion.div className="section-head" initial={{ opacity: 0, y: 35 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <span className="section-kicker">Coverage</span>
          <h2 className="section-title">South India rental network</h2>
          <p className="section-sub">Post and receive ride leads across popular travel regions.</p>
        </motion.div>

        <div className="loc-grid">
          {locations.map((location, index) => (
            <motion.div
              key={location}
              className="loc-card"
              initial={{ opacity: 0, x: index % 2 === 0 ? -35 : 35 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: index * 0.06 }}
              whileHover={{ scale: 1.04 }}
            >
              <MapPin size={21} />
              <span>{location}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── Careers ── */}
      <section className="section section-alt" id="careers">
        <motion.div className="section-head" initial={{ opacity: 0, y: 35 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <span className="section-kicker">Join our team</span>
          <h2 className="section-title">We're hiring drivers</h2>
          <p className="section-sub">Professional drivers across South India can apply to join the Cabnaina cab network.</p>
        </motion.div>

        <motion.div className="careers-card" initial={{ opacity: 0, y: 35 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <div className="careers-req-grid">
            <motion.div className="careers-req-item" whileHover={{ y: -5 }}>
              <span className="careers-req-icon"><ShieldCheck size={26} /></span>
              <h4>License</h4>
              <p>Valid batch license or heavy duty vehicle (HMV) license required</p>
            </motion.div>
            <motion.div className="careers-req-item" whileHover={{ y: -5 }}>
              <span className="careers-req-icon"><Star size={26} /></span>
              <h4>Experience</h4>
              <p>Minimum 2 years of professional driving experience</p>
            </motion.div>
            <motion.div className="careers-req-item" whileHover={{ y: -5 }}>
              <span className="careers-req-icon"><CheckCircle2 size={26} /></span>
              <h4>Uniform</h4>
              <p>White dress uniform is mandatory for all active drivers</p>
            </motion.div>
          </div>
          <div className="careers-cta">
            <p>Ready to drive with Cabnaina? Send your details on WhatsApp.</p>
            <a
              href={`${WA_LINK}?text=${encodeURIComponent("Hi Cabnaina, I want to apply as a driver.\nName:\nLicense type:\nExperience (years):\nCity:")}`}
              target="_blank"
              rel="noreferrer"
              className="btn-whatsapp"
            >
              <MessageCircle size={18} /> Apply via WhatsApp
            </a>
          </div>
        </motion.div>
      </section>

      {/* ── Contact ── */}
      <section className="section" id="contact">
        <motion.div className="contact-card" initial={{ opacity: 0, scale: 0.94 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
          <span className="contact-icon"><Users size={34} /></span>
          <h2>Start with Cabnaina</h2>
          <p>Call Jonesh or send the rental request, ad posting, or lead details through WhatsApp.</p>
          <div className="contact-btns">
            <a href={`tel:${PHONE}`} className="btn-primary"><Phone size={18} /> +91 81242 90402</a>
            <a href={WA_LINK} target="_blank" rel="noreferrer" className="btn-whatsapp"><MessageCircle size={18} /> Chat on WhatsApp</a>
          </div>
          <div className="service-tags">
            {["Rental", "Ads", "Drivers", "Tours", "24/7 Support"].map((tag) => (
              <span key={tag} className="tag"><CheckCircle2 size={14} /> {tag}</span>
            ))}
          </div>
        </motion.div>
      </section>

      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-logo"><img src="/favicon.svg" alt="" width="20" height="20" style={{ borderRadius: 5, verticalAlign: "middle" }} /> cab<b>naina</b></div>
          <p className="footer-credit">
            <span>Website created by</span>
            <a href="https://rajamuthaiah.netlify.app/" target="_blank" rel="noreferrer" aria-label="Rajamuthaiah website">
              <Globe2 size={13} /> Rajamuthaiah
            </a>
            <a href="https://www.linkedin.com/in/rajamuthaiahv" target="_blank" rel="noreferrer" aria-label="Rajamuthaiah LinkedIn">
              <span className="linkedin-glyph">in</span>
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
